/**
 * Townties API Module
 * Vanilla JS — Cart, Search, and Recommendations API
 */

'use strict';

/* ============================================================
   Utility: format money (mirrors Shopify's money_format)
   ============================================================ */
function formatMoney(cents) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  var value = (parseInt(cents, 10) / 100).toFixed(2);
  return '$' + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/* ============================================================
   Cart API
   ============================================================ */
var Cart = {
  /**
   * GET /cart.js — returns cart JSON
   */
  fetch: function() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function(res) {
      if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
      return res.json();
    });
  },

  /**
   * POST /cart/add.js
   * @param {number} variantId
   * @param {number} quantity
   * @param {object} properties  Optional line item properties
   */
  add: function(variantId, quantity, properties) {
    var body = { id: variantId, quantity: quantity || 1 };
    if (properties && Object.keys(properties).length) {
      body.properties = properties;
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function(res) {
      if (!res.ok) return res.json().then(function(err) { throw err; });
      return res.json();
    });
  },

  /**
   * POST /cart/change.js
   * @param {string} key   Line item key
   * @param {number} quantity  Set to 0 to remove
   */
  change: function(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function(res) {
      if (!res.ok) throw new Error('Cart change failed');
      return res.json();
    });
  },

  /**
   * POST /cart/update.js — update cart note or multiple quantities
   * @param {object} updates  e.g. { note: '...' } or { updates: {key: qty} }
   */
  update: function(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    }).then(function(res) {
      if (!res.ok) throw new Error('Cart update failed');
      return res.json();
    });
  },

  updateNote: function(note) {
    return Cart.update({ note: note });
  },

  /**
   * Render cart drawer from live cart data
   */
  render: function() {
    return Cart.fetch().then(function(cart) {
      Cart._updateCount(cart.item_count);
      Cart._updateSubtotal(cart.total_price);
      Cart._updateShippingProgress(cart);
      Cart._updateItems(cart);
      return cart;
    });
  },

  _updateShippingProgress: function(cart) {
    var el = document.getElementById('cart-shipping-progress');
    if (!el) return;

    if (cart.item_count === 0) {
      el.setAttribute('hidden', '');
      return;
    }
    el.removeAttribute('hidden');

    var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 5000;
    var remaining = Math.max(0, threshold - cart.total_price);
    var percent = Math.min(100, Math.round((cart.total_price / threshold) * 100));

    var messageEl = el.querySelector('.js-shipping-message');
    var fillEl = el.querySelector('.js-shipping-fill');

    if (remaining > 0) {
      el.classList.remove('is-unlocked');
      if (messageEl) {
        messageEl.innerHTML = "You're <strong>" + formatMoney(remaining) + "</strong> away from free shipping";
      }
    } else {
      el.classList.add('is-unlocked');
      if (messageEl) {
        messageEl.textContent = "You've unlocked free shipping";
      }
    }

    if (fillEl) fillEl.style.width = percent + '%';
  },

  _updateCount: function(count) {
    var badges = document.querySelectorAll('.js-cart-count');
    badges.forEach(function(el) {
      el.textContent = count;
      el.setAttribute('data-cart-count', count);
      if (count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
  },

  _updateSubtotal: function(totalPrice) {
    var el = document.querySelector('.js-cart-subtotal');
    if (el) el.textContent = formatMoney(totalPrice);
  },

  _updateItems: function(cart) {
    var listEl = document.getElementById('cart-items-list');
    var emptyEl = document.getElementById('cart-empty-state');
    var checkoutBtn = document.getElementById('cart-checkout-btn');

    if (!listEl) return;

    if (cart.item_count === 0) {
      listEl.setAttribute('hidden', '');
      if (emptyEl) emptyEl.removeAttribute('hidden');
      if (checkoutBtn) checkoutBtn.style.pointerEvents = 'none';
      return;
    }

    if (emptyEl) emptyEl.setAttribute('hidden', '');
    listEl.removeAttribute('hidden');
    if (checkoutBtn) checkoutBtn.style.pointerEvents = '';

    listEl.innerHTML = cart.items.map(function(item) {
      var variantInfo = item.variant_title !== 'Default Title' && item.variant_title
        ? item.variant_title
        : '';

      var propertiesHtml = '';
      if (item.properties) {
        Object.entries(item.properties).forEach(function(entry) {
          var key = entry[0], val = entry[1];
          if (!val) return;
          if (String(val).indexOf('/uploads/') !== -1) {
            propertiesHtml += '<div class="cart-item__variant">' + key + ': <a href="' + val + '" target="_blank">View file</a></div>';
          } else {
            propertiesHtml += '<div class="cart-item__variant">' + key + ': ' + val + '</div>';
          }
        });
      }

      return '<div class="cart-item" data-item-key="' + item.key + '">' +
        '<div class="cart-item__img">' +
          (item.image ? '<img src="' + item.image + '" alt="' + (item.title || '') + '" loading="lazy">' : '') +
        '</div>' +
        '<div class="cart-item__info">' +
          '<div class="cart-item__title">' + (item.product_title || item.title) + '</div>' +
          (variantInfo ? '<div class="cart-item__variant">' + variantInfo + '</div>' : '') +
          propertiesHtml +
          '<div class="cart-item__price">' + formatMoney(item.line_price) + '</div>' +
          '<button class="cart-item__remove js-cart-remove" data-item-key="' + item.key + '">Remove</button>' +
        '</div>' +
      '</div>';
    }).join('');

    // Re-attach remove listeners
    listEl.querySelectorAll('.js-cart-remove').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var key = btn.getAttribute('data-item-key');
        Cart.change(key, 0).then(function() {
          Cart.render();
          Cart.fetchUpsell();
        });
      });
    });
  },

  /**
   * Fetch product recommendation for upsell (gift box, etc.)
   */
  fetchUpsell: function() {
    return Cart.fetch().then(function(cart) {
      var upsellEl = document.getElementById('cart-upsell');
      if (!upsellEl) return;

      if (cart.item_count === 0) {
        upsellEl.innerHTML = '';
        return;
      }

      // Use first item's product id for recommendations
      var productId = cart.items[0].product_id;
      return fetch('/recommendations/products.json?product_id=' + productId + '&limit=4', {
        credentials: 'same-origin'
      }).then(function(res) { return res.json(); })
        .then(function(data) {
          if (!data.products || !data.products.length) {
            upsellEl.innerHTML = '';
            return;
          }
          // Show first recommendation as upsell
          var p = data.products[0];
          var variantId = p.variants && p.variants[0] ? p.variants[0].id : null;
          var price = p.variants && p.variants[0] ? formatMoney(p.variants[0].price) : '';
          var img = p.images && p.images[0] ? p.images[0].src : '';

          upsellEl.innerHTML =
            '<div class="upsell-block">' +
              '<div class="upsell-block__title">Complete Your Order</div>' +
              '<div class="upsell-item">' +
                '<div class="upsell-item__img">' + (img ? '<img src="' + img + '" alt="' + p.title + '" loading="lazy">' : '') + '</div>' +
                '<div class="upsell-item__text">' +
                  '<div>' + p.title + '</div>' +
                  '<div class="upsell-item__desc">' + (p.description ? p.description.replace(/<[^>]*>/g, '').substring(0, 80) + '…' : '') + '</div>' +
                '</div>' +
                '<span class="upsell-item__price">' + price + '</span>' +
                (variantId ? '<button class="upsell-add js-upsell-add" data-variant-id="' + variantId + '">+ Add</button>' : '') +
              '</div>' +
            '</div>';

          var addBtn = upsellEl.querySelector('.js-upsell-add');
          if (addBtn) {
            addBtn.addEventListener('click', function() {
              var vid = parseInt(addBtn.getAttribute('data-variant-id'));
              Cart.add(vid, 1).then(function() {
                Cart.render();
                addBtn.textContent = 'Added ✓';
                addBtn.disabled = true;
              }).catch(function() {
                addBtn.textContent = 'Error';
              });
            });
          }
        });
    });
  }
};

/* ============================================================
   Search API — Predictive Search
   ============================================================ */
var Search = {
  _debounceTimer: null,

  /**
   * Fetch predictive search suggestions
   * @param {string} query
   */
  predict: function(query, containerId) {
    if (!query || query.length < 2) {
      Search.clearResults(containerId);
      return Promise.resolve([]);
    }

    return fetch('/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function(res) {
      if (!res.ok) throw new Error('Search failed');
      return res.json();
    }).then(function(data) {
      var products = (data.resources && data.resources.results && data.resources.results.products) || [];
      Search.renderResults(products, query, containerId);
      return products;
    });
  },

  renderResults: function(products, query, containerId) {
    var el = document.getElementById(containerId || 'predictive-results');
    if (!el) return;

    if (!products.length) {
      el.innerHTML = '<div style="padding:16px;font-size:13px;color:var(--color-muted);">No results for "' + query + '"</div>';
      el.removeAttribute('hidden');
      return;
    }

    el.innerHTML = products.map(function(p) {
      var price = p.price ? formatMoney(p.price) : '';
      var img = p.featured_image && p.featured_image.url ? p.featured_image.url : '';
      return '<a href="' + p.url + '" class="predictive-result-item" role="option">' +
        (img ? '<img class="predictive-result-img" src="' + img + '" alt="' + p.title + '" loading="lazy">' : '') +
        '<div>' +
          '<div class="predictive-result-title">' + p.title + '</div>' +
          (price ? '<div class="predictive-result-price">' + price + '</div>' : '') +
        '</div>' +
      '</a>';
    }).join('');

    el.removeAttribute('hidden');
  },

  clearResults: function(containerId) {
    var el = document.getElementById(containerId || 'predictive-results');
    if (el) {
      el.innerHTML = '';
      el.setAttribute('hidden', '');
    }
  },

  /**
   * Debounce input for predictive search
   * @param {string} query
   * @param {string} [containerId] - target dropdown id (defaults to 'predictive-results')
   */
  debounce: function(query, containerId) {
    clearTimeout(Search._debounceTimer);
    if (!query || query.length < 2) {
      Search.clearResults(containerId);
      return;
    }
    Search._debounceTimer = setTimeout(function() {
      Search.predict(query, containerId);
    }, 200);
  }
};

/* ============================================================
   Export for use by theme.js and tests
   ============================================================ */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { Cart: Cart, Search: Search, formatMoney: formatMoney };
}
